/**
 * Schedule a follow-up build-stream POST after client-side tools finish.
 * Used when the assistant stream ends with tool calls (success or failed build).
 */

import { shouldContinueEditStream } from './editLoopCompleteness';
import { safeLastAssistantMessageIsCompleteWithToolCalls } from './safeLastAssistantMessageIsCompleteWithToolCalls';
import { lastAssistantHasPendingClientTools } from './lastAssistantHasPendingClientTools';
import { lastToolStepBuildFailed } from './lastAssistantClientBuildFailedInUiMessages';

export type StreamContinuationContext = {
  messages: readonly unknown[];
  toolSteps: readonly {
    toolName: string;
    isComplete: boolean;
    result?: string;
    args?: Record<string, unknown>;
  }[];
  chatStatus: string;
  buildRepeatCount: number;
  maxBuildRepeatErrors: number;
  toolContinuationCount: number;
  maxToolContinuations: number;
  /** Set when a client tool finished and its output still needs to be POSTed to Chef. */
  toolResultContinuationPending: boolean;
  /** planComplete.files paths for the current edit turn — empty when not an edit plan. */
  plannedFilePaths?: readonly string[];
  /** Assistant stream ended on `finishReason: "length"`, so the turn is unfinished. */
  truncated?: boolean;
  /** A client tool (writeFile/build) is executing right now. */
  clientToolsBusy?: boolean;
};

export type StreamContinuationDecision =
  | { action: 'wait' }
  | { action: 'send' }
  | { action: 'stop'; reason: 'max_retries' | 'not_ready' };

/** Pure decision helper — unit tested without timers. */
export function evaluateStreamContinuation(
  ctx: StreamContinuationContext
): StreamContinuationDecision {
  if (ctx.buildRepeatCount >= ctx.maxBuildRepeatErrors) {
    return { action: 'stop', reason: 'max_retries' };
  }

  if (ctx.toolContinuationCount >= ctx.maxToolContinuations) {
    return { action: 'stop', reason: 'max_retries' };
  }

  const failedBuild = lastToolStepBuildFailed(ctx.toolSteps);
  const truncated = ctx.truncated === true;

  // A client tool that's still genuinely executing (e.g. `build`, mid-run) must hold every
  // other decision back — checked before `editStreamOpen`/`not_ready` below, because
  // `shouldContinueEditStream`'s `hasBuildToolStep` treats ANY `build` tool step as "stop
  // waiting for edits" the moment it's registered, complete or not. Without this ordering,
  // a `not_ready` stop can fire while `build` is still running, and the caller's cleanup
  // (`markStreamEndedWithoutSuccessfulBuild`) then force-marks that still-running segment as
  // an error a few seconds before the real result arrives and corrects it.
  //
  // A cutoff strands tool parts mid-input, and those never execute — so for a truncated
  // turn the message parts are not a usable "still busy" signal and only a tool that is
  // genuinely running may hold the continuation back.
  const toolsInFlight = truncated
    ? ctx.clientToolsBusy === true
    : lastAssistantHasPendingClientTools(ctx.messages);
  if (toolsInFlight && !failedBuild) {
    return { action: 'wait' };
  }

  const editStreamOpen = shouldContinueEditStream({
    messages: ctx.messages,
    toolSteps: ctx.toolSteps,
    plannedFilePaths: ctx.plannedFilePaths ?? [],
    toolResultContinuationPending: ctx.toolResultContinuationPending,
    clientToolsBusy: ctx.clientToolsBusy,
  });

  if (!failedBuild && !truncated && !ctx.toolResultContinuationPending && !editStreamOpen) {
    return { action: 'stop', reason: 'not_ready' };
  }

  if (ctx.chatStatus === 'streaming' || ctx.chatStatus === 'submitted') {
    return { action: 'wait' };
  }

  const toolsComplete = safeLastAssistantMessageIsCompleteWithToolCalls({ messages: ctx.messages });
  if (!toolsComplete && !failedBuild && !truncated && !editStreamOpen) {
    return { action: 'wait' };
  }

  if (!toolsComplete && failedBuild) {
    const lastBuild = [...ctx.toolSteps]
      .reverse()
      .find((s) => s.toolName === 'build' && s.isComplete);
    if (!lastBuild?.result) {
      return { action: 'wait' };
    }
  }

  return { action: 'send' };
}

export type ScheduleStreamContinuationOptions = {
  getMessages: () => readonly unknown[];
  getToolSteps: () => readonly {
    toolName: string;
    isComplete: boolean;
    result?: string;
    args?: Record<string, unknown>;
  }[];
  getChatStatus: () => string;
  getBuildRepeatCount: () => number;
  getToolResultContinuationPending: () => boolean;
  getToolContinuationCount?: () => number;
  getPlannedFilePaths?: () => readonly string[];
  getTruncated?: () => boolean;
  getClientToolsBusy?: () => boolean;
  maxBuildRepeatErrors: number;
  maxToolContinuations?: number | (() => number);
  send: () => void;
  onStreaming: (streaming: boolean) => void;
  onExhausted?: (reason: 'max_retries' | 'not_ready' | 'max_wait') => void;
  /**
   * Wakes the scheduler when something it waits on changed (chat status, a finished tool, the
   * busy flag). Returns an unsubscribe function. Without it the scheduler only polls.
   */
  subscribe?: (wake: () => void) => () => void;
  /** Give up after waiting this long while the tab is visible (default 2 minutes). */
  maxWaitMs?: number;
  /** Fallback poll between wake-ups (default 250 ms). */
  retryDelayMs?: number;
};

/**
 * Returns a cancel function. Only one continuation scheduler runs at a time per hook instance.
 *
 * Event-driven (PROTOTYPER_STABILITY_TECH_PLAN.md P6.4d, FE-7): a `subscribe` wake-up or the tab
 * becoming visible re-evaluates at once, and a slow poll is the fallback. The wait is bounded in
 * time, not attempts, and only counts while the tab is visible. The old cap of 120 attempts
 * 50 ms apart ran out after about 6 s of a long `build` and ended a healthy turn; and background
 * tabs throttle timers, so hidden time must not end a turn nobody is watching.
 */
export function scheduleStreamContinuation(opts: ScheduleStreamContinuationOptions): () => void {
  const maxWaitMs = opts.maxWaitMs ?? 120_000;
  const retryDelayMs = opts.retryDelayMs ?? 250;
  const startedAt = Date.now();
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let unsubscribe: (() => void) | undefined;

  const isHidden = (): boolean =>
    typeof document !== 'undefined' && document.visibilityState === 'hidden';

  const stop = (): void => {
    cancelled = true;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    unsubscribe?.();
    unsubscribe = undefined;
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };

  const run = (): void => {
    timer = null;
    if (cancelled) return;

    const maxToolContinuations =
      typeof opts.maxToolContinuations === 'function'
        ? opts.maxToolContinuations()
        : (opts.maxToolContinuations ?? 12);

    const decision = evaluateStreamContinuation({
      messages: opts.getMessages(),
      toolSteps: opts.getToolSteps(),
      chatStatus: opts.getChatStatus(),
      buildRepeatCount: opts.getBuildRepeatCount(),
      toolContinuationCount: opts.getToolContinuationCount?.() ?? 0,
      maxToolContinuations,
      toolResultContinuationPending: opts.getToolResultContinuationPending(),
      plannedFilePaths: opts.getPlannedFilePaths?.() ?? [],
      truncated: opts.getTruncated?.() ?? false,
      clientToolsBusy: opts.getClientToolsBusy?.() ?? false,
      maxBuildRepeatErrors: opts.maxBuildRepeatErrors,
    });

    if (decision.action === 'send') {
      stop();
      opts.onStreaming(true);
      opts.send();
      return;
    }

    if (decision.action === 'stop') {
      stop();
      opts.onExhausted?.(decision.reason);
      return;
    }

    if (!isHidden() && Date.now() - startedAt >= maxWaitMs) {
      stop();
      opts.onExhausted?.('max_wait');
      return;
    }
    timer = setTimeout(run, retryDelayMs);
  };

  /** Re-evaluate on the next tick, never inside the caller's own callback (e.g. `onToolCall`). */
  const wake = (): void => {
    if (cancelled) return;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(run, 0);
  };

  function onVisibilityChange(): void {
    if (!isHidden()) wake();
  }

  unsubscribe = opts.subscribe?.(wake);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  run();

  return stop;
}
